// components/Login.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
import toast from 'react-hot-toast';
import { Lock, User, LogIn, Building2, Shield, Sparkles, ChevronRight } from 'lucide-react';
import logo from '../assets/Y1.jpeg';
import apiClient from '../api/client';

const Login = () => {
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // ✅ Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      console.log('✅ Token found, redirecting to dashboard...');
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      console.log('🔐 Attempting login with:', credentials.username);
      
      // ✅ Direct API call
      // const response = await axios.post('http://localhost:5000/api/login', credentials);
      const response = await apiClient.post(
  '/login',
  credentials
);
      
      console.log('✅ Login Response:', response.data);
      
      if (response.data && response.data.token) {
        // Save token and user data
        localStorage.setItem('token', response.data.token);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        
        toast.success('Welcome back! 🎉');
        
        // ✅ Force navigation
        window.location.href = '/dashboard';
      } else {
        toast.error('Invalid response from server');
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      
      if (error.response) {
        toast.error(error.response.data?.error || 'Invalid credentials');
      } else if (error.request) {
        toast.error('Server not responding. Please check if backend is running.');
      } else {
        toast.error('Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden">
      {/* Background Image with Overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: 'url("https://images.unsplash.com/photo-1556761175-b413da4baf72?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1974&q=80")',
          filter: 'brightness(0.8) saturate(1.1)'
        }}
      ></div>

      {/* Light Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/30 via-blue-700/20 to-blue-500/10"></div>

      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-400 rounded-full mix-blend-multiply filter blur-3xl opacity-5 animate-pulse delay-2000"></div>
        
        {/* Floating Particles */}
        <div className="absolute top-10 left-10 w-2 h-2 bg-white rounded-full opacity-40 animate-float"></div>
        <div className="absolute top-20 right-20 w-3 h-3 bg-white rounded-full opacity-20 animate-float-delay"></div>
        <div className="absolute bottom-20 left-20 w-2 h-2 bg-white rounded-full opacity-30 animate-float"></div>
        <div className="absolute bottom-10 right-10 w-3 h-3 bg-white rounded-full opacity-20 animate-float-delay"></div>
      </div>

      {/* Main Login Card */}
      <div className="bg-white/20 backdrop-blur-xl p-8 rounded-2xl shadow-2xl w-96 relative z-10 border border-white/30 transform transition-all duration-500 hover:scale-[1.02]">
        {/* Decorative Top Bar */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-400 via-purple-400 to-blue-400 rounded-t-2xl"></div>

        {/* Company Logo */}
        <div className="flex justify-center mt-2 mb-4">
          <div className="relative group">
            <div className="w-24 h-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 p-1 shadow-2xl group-hover:shadow-3xl transition-all duration-300">
              <div className="w-full h-full rounded-full bg-white/90 backdrop-blur-sm p-2 flex items-center justify-center overflow-hidden">
                <img 
                  src={logo} 
                  alt="Yantranity" 
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
            </div>
            <div className="absolute -bottom-1 -right-1 bg-green-500 rounded-full p-1 shadow-lg animate-bounce">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <div className="absolute -top-1 -right-1">
              <Sparkles className="w-5 h-5 text-yellow-400 animate-pulse" />
            </div>
          </div>
        </div>

        {/* Company Name */}
        <div className="text-center mb-4">
          <h1 className="text-3xl font-bold text-white drop-shadow-lg tracking-tight">
            YANTRANITEE
          </h1>
          <p className="text-sm text-white/80 mt-1 flex items-center justify-center gap-2 drop-shadow-md">
            <Building2 className="w-4 h-4" />
            Customer Management System
          </p>
        </div>

        {/* Divider */}
        <div className="relative mb-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-white/20"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-transparent text-white/70 flex items-center gap-1 backdrop-blur-sm">
              <Lock className="w-3 h-3" />
              Secure Login
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Username Field */}
          <div className="mb-4">
            <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2 drop-shadow-md">
              <User className="w-4 h-4 text-white" />
              Username
            </label>
            <div className="relative group">
              <input
                type="text"
                className="w-full pl-10 pr-3 py-3 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-gray-800 placeholder-gray-500 bg-white/80 backdrop-blur-sm transition-all duration-200 group-hover:bg-white/90"
                placeholder="Enter your username"
                value={credentials.username}
                onChange={(e) => setCredentials({...credentials, username: e.target.value})}
                required
              />
              <User className="absolute left-3 top-3.5 w-5 h-5 text-gray-500 group-hover:text-blue-600 transition-colors duration-200" />
            </div>
          </div>
          
          {/* Password Field */}
          <div className="mb-6">
            <label className="block text-white text-sm font-semibold mb-2 flex items-center gap-2 drop-shadow-md">
              <Lock className="w-4 h-4 text-white" />
              Password
            </label>
            <div className="relative group">
              <input
                type={showPassword ? "text" : "password"}
                className="w-full pl-10 pr-12 py-3 border border-white/30 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-gray-800 placeholder-gray-500 bg-white/80 backdrop-blur-sm transition-all duration-200 group-hover:bg-white/90"
                placeholder="Enter your password"
                value={credentials.password}
                onChange={(e) => setCredentials({...credentials, password: e.target.value})}
                required
              />
              <Lock className="absolute left-3 top-3.5 w-5 h-5 text-gray-500 group-hover:text-blue-600 transition-colors duration-200" />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-3 text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          {/* Login Button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500/90 to-blue-600/90 hover:from-blue-600/90 hover:to-blue-700/90 backdrop-blur-sm text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 text-lg shadow-xl hover:shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 group relative overflow-hidden border border-white/20"
          >
            {/* Button Shine Effect */}
            <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
            
            {loading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Logging in...
              </>
            ) : (
              <>
                <LogIn className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                Login
                <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </>
            )}
          </button>
        </form>
        
        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-white/20">
          <div className="flex justify-between items-center text-xs text-white/70">
            <span className="flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse inline-block"></span>
              Default: admin / admin123
            </span>
            <span className="flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Secured
            </span>
          </div>
          <div className="text-center mt-3 text-xs text-white/50">
            © 2026 Yantranity. All rights reserved.
          </div>
        </div>
      </div>

      {/* Custom animation styles */}
      <style jsx>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes float-delay {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delay {
          animation: float-delay 4s ease-in-out infinite;
        }
        input::placeholder {
          color: rgba(107, 114, 128, 0.7) !important;
        }
        input:-webkit-autofill {
          -webkit-box-shadow: 0 0 0 30px rgba(255, 255, 255, 0.9) inset !important;
          -webkit-text-fill-color: #1f2937 !important;
        }
      `}</style>
    </div>
  );
};

export default Login;